import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { TenantProvider } from "@/context/TenantContext";
import AppShell from "@/components/AppShell";
import LoginPage from "@/pages/LoginPage";
import GoogleAuthCallback from "@/pages/GoogleAuthCallback";
import ExecutiveDashboard from "@/pages/ExecutiveDashboard";
import BoardSummary from "@/pages/BoardSummary";
import RequirementsPage from "@/pages/RequirementsPage";
import EvidencePage from "@/pages/EvidencePage";
import EnterpriseReport from "@/pages/EnterpriseReport";
import FrameworkReportPage from "@/pages/FrameworkReportPage";
import {
  ManagementSummary, CertificationPage, RiskRegisterPage,
  ImprovementPage, RoadmapPage, ScoringGuidePage
} from "@/pages/ReportPages";
import { KnowledgePage, GlossaryPage } from "@/pages/ReferencePages";
import AIRecommendationsPage from "@/pages/AIRecommendationsPage";
import {
  UsersPage, TenantsPage, OrganizationSettingsPage, AuditLogPage
} from "@/pages/AdminPages";
import { Spinner } from "@/components/ui-parts";

function Protected({ children, roles }) {
  const { user, status } = useAuth();
  if (status === "loading") {
    return <div className="min-h-screen flex items-center justify-center bg-[#050914]"><Spinner label="Checking session..." /></div>;
  }
  if (status !== "authenticated" || !user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <TenantProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </TenantProvider>
    </AuthProvider>
  );
}

function AppRouter() {
  const location = useLocation();
  // Handle Emergent Google OAuth callback (URL fragment carries session_id)
  if (location.hash?.includes("session_id=")) {
    return <GoogleAuthCallback />;
  }
  return (
    <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Protected><AppShell><ExecutiveDashboard /></AppShell></Protected>} />
            <Route path="/board" element={<Protected><AppShell><BoardSummary /></AppShell></Protected>} />
            <Route path="/requirements" element={<Protected><AppShell><RequirementsPage /></AppShell></Protected>} />
            <Route path="/evidence" element={<Protected><AppShell><EvidencePage /></AppShell></Protected>} />
            <Route path="/scoring" element={<Protected><AppShell><ScoringGuidePage /></AppShell></Protected>} />
            <Route path="/report" element={<Protected><AppShell><EnterpriseReport /></AppShell></Protected>} />
            <Route path="/framework-report" element={<Protected><AppShell><FrameworkReportPage /></AppShell></Protected>} />
            <Route path="/management" element={<Protected><AppShell><ManagementSummary /></AppShell></Protected>} />
            <Route path="/certification" element={<Protected><AppShell><CertificationPage /></AppShell></Protected>} />
            <Route path="/risk-register" element={<Protected><AppShell><RiskRegisterPage /></AppShell></Protected>} />
            <Route path="/improvement" element={<Protected><AppShell><ImprovementPage /></AppShell></Protected>} />
            <Route path="/roadmap" element={<Protected><AppShell><RoadmapPage /></AppShell></Protected>} />
            <Route path="/ai" element={<Protected><AppShell><AIRecommendationsPage /></AppShell></Protected>} />
            <Route path="/knowledge" element={<Protected><AppShell><KnowledgePage /></AppShell></Protected>} />
            <Route path="/glossary" element={<Protected><AppShell><GlossaryPage /></AppShell></Protected>} />
            <Route path="/organization" element={<Protected roles={["admin","superadmin"]}><AppShell><OrganizationSettingsPage /></AppShell></Protected>} />
            <Route path="/users" element={<Protected roles={["admin","superadmin"]}><AppShell><UsersPage /></AppShell></Protected>} />
            <Route path="/tenants" element={<Protected roles={["superadmin"]}><AppShell><TenantsPage /></AppShell></Protected>} />
            <Route path="/audit" element={<Protected><AppShell><AuditLogPage /></AppShell></Protected>} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function LoginRoute() {
  const { status } = useAuth();
  if (status === "authenticated") return <Navigate to="/dashboard" replace />;
  return <LoginPage />;
}

export default App;
